import { useFormik } from 'formik';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import {
  GetAccountInfoDocument,
  GetAccountInfoQuery,
  GetAccountInfoQueryVariables,
  useApplySymbolMutation,
  useConfirmApplySymbolMutation,
  useGetDateTimeQuery,
  useGetDomesticRealEstateSymbolInfoQuery,
} from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import * as Yup from 'yup';
import { Client, Context as UrqlContext } from 'urql';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { create } from 'zustand';
import { ITExplanationModal } from './symbolDetail/modal';
import { useMessageModal } from 'views/mutual/modal/messageModal/hooks';
import { InputMeetingDateTimeProps } from './symbolDetail/modal/ItDisclosureApplicationDateSelection/hooks';
import { DomesticRealEstateSymbolDetailInfo } from 'views/utils/graphql/graphql';
import { ITExplanationModalStep } from './symbolDetail/modal/hooks';
import { useGlobalLoadingHandler } from 'views/mutual/globalLoading/hook';
import { ErrorWord } from 'views/templates/accountOpen/common';
import { DateFormatType, formatDate } from 'views/utils/converter';
import { ApplySymbolRequestType, ApplyAssetType, AccountType } from 'views/utils/enums';

export enum SymbolPageStep {
  Detail,
  CompatibilityCheck,
  Confirm,
  Complete,
  ContractComplete,
  ApplicationStatus,
  Error,
}

export type PurchaseSimulationInput = {
  symbol: string;
  amount: number | null;
  applicationStartDateTime: string;
};

const limitDateCheck = (today: Date, limitDate: Date | null): boolean => {
  if (!limitDate) return false;

  const addDays = 1;
  limitDate.setDate(limitDate.getDate() + addDays);

  return today < limitDate;
};

// falseの場合は条件を満たしていないので申し込めない状態
const canApplyItExplanation = (
  symbol: DomesticRealEstateSymbolDetailInfo | null | undefined,
  input: PurchaseSimulationInput,
): boolean => {
  if (
    symbol &&
    // apiのレスポンスとして申し込めるかのフラグがある
    symbol.Tradable
    // && input.isCheck1
  ) {
    return true;
  } else {
    return false;
  }
};

type SymbolPageProps = {
  symbol: string;
};

export const useDomesticRealEstateItExplanationSymbolPage = ({ symbol }: SymbolPageProps) => {
  const [symbolPageStep, setSymbolPageStep] = useState<SymbolPageStep>(SymbolPageStep.Detail);
  const { showLoading, hideLoading } = useGlobalLoadingHandler();
  const [applySymbolRes, applySymbol] = useApplySymbolMutation();
  const [confirmApplySymbolRes, confirmApplySymbol] = useConfirmApplySymbolMutation();
  const client = useContext(UrqlContext) as Client;

  const [symbolDetailInfo, _reload] = useGetDomesticRealEstateSymbolInfoQuery({
    variables: {
      symbol,
    },
    requestPolicy: 'network-only',
  });
  const [dateTime, getDataTime] = useGetDateTimeQuery({
    requestPolicy: 'network-only',
  });
  const { showError, messageClose } = useGlobalToastHandler();
  const { modalProps, showMessageModal } = useMessageModal();
  const { param, add } = useITExplanationPageStore();

  const AmountInputValidation = Yup.object().shape({
    symbol: Yup.string().required(),
    amount: Yup.number().required(ErrorWord.SELECT),
  });

  const showCustomMessageModal = (input: PurchaseSimulationInput, initialModalStep?: ITExplanationModalStep) => {
    showMessageModal(
      <ITExplanationModal
        amount={input.amount!}
        initialModalStep={initialModalStep}
        onClosed={async (nextSymbolPageStep, inputDateTime) => {
          //日付選択後のclose処理
          if (nextSymbolPageStep === SymbolPageStep.Confirm && inputDateTime) {
            add({
              ...param,
              ...input,
              ...inputDateTime,
            });

            //残高チェック
            if (
              !symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo?.RemainingQuantity ||
              symbolDetailInfo.data.GetDomesticRealEstateSymbolInfo.RemainingQuantity < input.amount!
            ) {
              showError('残高が不足しています');
              modalProps.setIsOpen(false);
              return;
            }

            //テスト用にコメントアウト
            //IT重説APIにリクエスト（確認）
            const appSymbolRes = await confirmApplySymbol({
              input: {
                Symbol: input.symbol!,
                Amount: input.amount!,
                DesiredApplicationDates: {
                  Date1: formatDate(inputDateTime.meetingDate1, DateFormatType.Slash_yyyyMMdd),
                  Time1: inputDateTime.meetingTime1!,
                  Date2: formatDate(inputDateTime.meetingDate2, DateFormatType.Slash_yyyyMMdd),
                  Time2: inputDateTime.meetingTime2!,
                  Date3: formatDate(inputDateTime.meetingDate3, DateFormatType.Slash_yyyyMMdd),
                  Time3: inputDateTime.meetingTime3!,
                },
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Confirm,
                AssetType: ApplyAssetType.ITExplanation,
                ApplicationStartDate: input.applicationStartDateTime,
              },
            });
            if (!appSymbolRes?.data) return;
            if (appSymbolRes.data.ApplySymbol?.Code === 0) {
              modalProps.setIsOpen(false);
              setSymbolPageStep(SymbolPageStep.Confirm);
            } else {
              showError(appSymbolRes.data.ApplySymbol?.Message);
            }
          } else {
            //適合性チェックと配当金振込用口座入力画面が対象
            setSymbolPageStep(nextSymbolPageStep);
          }
        }}
        symbolDetail={symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo!}
        today={new Date(input.applicationStartDateTime)}
      />,
      () => {
        // もし承諾有無を送る場合初期化
      },
      'キャンセル',
    );
  };

  const onSubmit = useOnceSubmit<PurchaseSimulationInput>(
    {
      onPreviousSubmit: async (input) => {
        return canApplyItExplanation(symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo, input);
      },
      onSubmit: async (input) => {
        switch (symbolPageStep) {
          case SymbolPageStep.Detail: {
            // ConfirmでAPIのエラー → Detailに戻って修正 → Confirmに遷移した場合はエラーが消えるようにする
            messageClose();
            getDataTime();

            showLoading();

            const accountInfo = await client.query<GetAccountInfoQuery, GetAccountInfoQueryVariables>(
              GetAccountInfoDocument,
              {},
              {
                requestPolicy: 'network-only',
              },
            );
            hideLoading();

            const isElderly = accountInfo.data?.GetAccountInfo?.IsElderly;
            const isPersonal = accountInfo.data?.GetAccountInfo?.AccountType == AccountType.Personal;
            const hasValidFatcaAnswer = accountInfo.data?.GetAccountInfo?.ApplicationStatus?.HasValidFatcaAnswer;
            const hasValidPEPsAnswer = accountInfo.data?.GetAccountInfo?.ApplicationStatus?.HasValidPEPsAnswer;

            // 高齢者フラグチェック
            // 個人口座の場合、FATCAとPEPsチェック
            const shouldShowError =
            hasValidFatcaAnswer === false || (isPersonal && (isElderly !== false || hasValidPEPsAnswer === false));

            if (shouldShowError) {
              setSymbolPageStep(SymbolPageStep.Error);
              return;
            }

            //必須項目チェック
            if (
              !(
                accountInfo.data?.GetAccountInfo?.ApplicationStatus?.HasAnsweredDividendAccount &&
                accountInfo.data?.GetAccountInfo?.ApplicationStatus?.HasValidFatcaAnswer != null &&
                accountInfo.data?.GetAccountInfo?.ApplicationStatus?.HasAnsweredInvoiceNumber
              )
            ) {
              setSymbolPageStep(SymbolPageStep.ApplicationStatus);
              return;
            }
            setSymbolPageStep(SymbolPageStep.Detail);
            showCustomMessageModal(input);
            break;
          }
          case SymbolPageStep.ApplicationStatus: {
            setSymbolPageStep(SymbolPageStep.Detail);
            showCustomMessageModal(input);
            break;
          }
          case SymbolPageStep.CompatibilityCheck: {
            setSymbolPageStep(SymbolPageStep.Detail);
            showCustomMessageModal(input, ITExplanationModalStep.ELECTRONIC_DELIVERY_OF_DISCLOSURE_DOCUMENT);
            break;
          }
          case SymbolPageStep.Confirm: {
            //テスト用にコメントアウト
            const appSymbolRes = await applySymbol({
              input: {
                Symbol: param.symbol!,
                Amount: param.amount!,
                DesiredApplicationDates: {
                  Date1: formatDate(param.meetingDate1, DateFormatType.Slash_yyyyMMdd),
                  Time1: param.meetingTime1!,
                  Date2: formatDate(param.meetingDate2, DateFormatType.Slash_yyyyMMdd),
                  Time2: param.meetingTime2!,
                  Date3: formatDate(param.meetingDate3, DateFormatType.Slash_yyyyMMdd),
                  Time3: param.meetingTime3!,
                },
                IntroductionConfirmation: 3, //この機能は現在廃止しているため3で固定
                RequestType: ApplySymbolRequestType.Apply,
                AssetType: ApplyAssetType.ITExplanation,
                ApplicationStartDate: input.applicationStartDateTime,
              },
            });
            if (!appSymbolRes?.data) return;
            if (appSymbolRes.data.ApplySymbol?.Code === 0) {
              setSymbolPageStep(SymbolPageStep.Complete);
            } else {
              showError(appSymbolRes.data.ApplySymbol?.Message);
            }
            break;
          }
          case SymbolPageStep.Complete:
            break;
        }
      },
    },
    [symbolPageStep, setSymbolPageStep, showError, messageClose], //applyItExplanation,
  );

  const backDetailPage = useCallback(() => {
    switch (symbolPageStep) {
      case SymbolPageStep.Detail: {
        break;
      }
      case SymbolPageStep.Confirm: {
        setSymbolPageStep(SymbolPageStep.Detail);
        break;
      }
      case SymbolPageStep.Complete:
        break;
    }
  }, [symbolPageStep, setSymbolPageStep]);

  const formik = useFormik<PurchaseSimulationInput>({
    initialValues: {
      symbol,
      amount: null,
      applicationStartDateTime: '',
    },
    onSubmit,
    validationSchema: AmountInputValidation,
    validateOnBlur: true,
    validateOnChange: true,
  });

  useEffect(() => {
    if (!dateTime.data?.GetDateTime) return;
    formik.values.applicationStartDateTime = dateTime.data?.GetDateTime?.Current!;
  }, [dateTime]);

  const limitDateResult = limitDateCheck(
    new Date(formik.values.applicationStartDateTime),
    symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo?.ApplicationLimitDate
      ? new Date(symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo.ApplicationLimitDate)
      : null,
  );

  const canApplyItExplanationResult =
    canApplyItExplanation(symbolDetailInfo.data?.GetDomesticRealEstateSymbolInfo, formik.values) && limitDateResult;

  const lotInputControlProps = {
    disabled: !canApplyItExplanationResult,
    error: formik.touched.amount && typeof formik.errors.amount === 'string' ? formik.errors.amount : undefined,
    value: formik.values.amount,
    onChange: (value: string) => formik.setFieldValue('amount', Number(value)),
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    symbolPageStep,
    symbolDetailInfo,
    formik,
    canApplyItExplanation: canApplyItExplanationResult,
    backDetailPage,
    lotInputControlProps,
    modalProps,
  };
};

/*--------------
  口座開設
  --------------*/
//入力データ管理
type ITExplanationPageStore = {
  param: ItExplanationEstablishRequestParam;
  add: (data: ItExplanationEstablishRequestParam) => void;
  clear: () => void;
};

export const useITExplanationPageStore = create<ITExplanationPageStore>((set) => ({
  param: {} as ItExplanationEstablishRequestParam,
  add: (data: ItExplanationEstablishRequestParam) =>
    // 前のデータは残してセットしたい
    set((state) => ({ param: { ...state.param, ...data } })),
  clear: () => set({ param: {} as ItExplanationEstablishRequestParam }),
}));

export type ItExplanationEstablishRequestParam = PurchaseSimulationInput & InputMeetingDateTimeProps;
